import { Camera as CapacitorCamera } from '@capacitor/camera'
import { CameraPreview } from '@capacitor-community/camera-preview'
import { CameraPreviewOptions } from '@capacitor-community/camera-preview/dist/esm/definitions'
import { IonFooter, IonHeader, IonPage } from '@ionic/react'
import { camera, cameraReverse, close } from 'ionicons/icons'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { isMobileApp } from '~/shared/config/constants'
import { Button, IconButton } from '~/shared/ui/Buttons'
import { BODY_MAX_WIDTH, myPalette } from '~/shared/ui/theme'
import { CameraContext } from './context'
import { PreviewPhoto } from './PreviewPhoto'
import {
  IonToolbar,
  CameraAltButton,
  ToolbarWrapper,
  ErrorMessage,
  CameraPreviewContent,
  HelperText,
  ToolbarContent,
} from './styled'

type CameraProviderProps = {
  children: ReactNode
}

export function CameraProvider({ children }: CameraProviderProps) {
  const [isPermissionError, togglePermissionError] = useState(false)
  const [cameraPosition, setCameraPosition] = useState<'rear' | 'front'>('rear')
  const [isCameraOpen, toggleIsCameraOpen] = useState(false)
  const [helperText, setHelperText] = useState<string | null>(null)
  const [photo, setPhoto] = useState<Blob>()
  const [isLoading, setIsLoading] = useState(false)

  const [cameraParams, setCameraParams] = useState<{
    fn: (file: Blob) => Promise<void>
  }>()

  const cameraOptions: CameraPreviewOptions = useMemo(
    () => ({
      parent: 'cameraPreview',
      position: cameraPosition,
      toBack: true,
      disableAudio: true,
      enableHighResolution: true,
      enableOpacity: true,
      ...(isMobileApp
        ? {
            enableZoom: true,
            lockAndroidOrientation: true,
            rotateWhenOrientationChanged: false,
          }
        : {
            height: 1920,
          }),
    }),
    [cameraPosition],
  )

  const handleCameraStart = useCallback(async () => {
    const permission = await CapacitorCamera.checkPermissions()

    if (permission.camera === 'denied') {
      togglePermissionError(true)
      return
    }

    await CameraPreview.start(cameraOptions)
  }, [cameraOptions])

  const handleCameraOpen = useCallback(
    async (fn: (file: Blob) => Promise<void>) => {
      setCameraParams({ fn })
      toggleIsCameraOpen(true)
      handleCameraStart()
    },
    [handleCameraStart],
  )

  const handleCameraClose = useCallback(async () => {
    try {
      await CameraPreview.stop()
    } finally {
      toggleIsCameraOpen(false)
      setCameraParams(undefined)
      setPhoto(undefined)
    }
  }, [])

  const handlePhotoDelete = () => {
    setPhoto(undefined)
  }

  const handlePhotoSave = async (file: Blob) => {
    try {
      setIsLoading(true)
      await cameraParams?.fn?.(file)
      setPhoto(undefined)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCameraFlip = async () => {
    if (isMobileApp) {
      await CameraPreview.flip()
    } else {
      const position = cameraPosition === 'rear' ? 'front' : 'rear'
      setCameraPosition(position)

      await CameraPreview.stop()
      await CameraPreview.start({ ...cameraOptions, position })
    }
  }

  const handleCameraCapture = async () => {
    const res = await CameraPreview.capture({
      quality: 100,
    })
    const fileString = `data:image/png;base64,${res.value}`

    const response = await fetch(fileString as string).then((res) => res.blob())
    setPhoto(response)
  }

  return (
    <>
      {isCameraOpen && (
        <IonPage
          style={{ margin: '0 auto', maxWidth: BODY_MAX_WIDTH, width: '100%' }}
        >
          <IonHeader style={{ backgroundColor: myPalette.dark.shadowLight }}>
            <IonToolbar>
              <IconButton
                slot='start'
                onClick={handleCameraClose}
                icon={close}
                style={{ fontSize: '22px' }}
                iconProps={{
                  style: { color: myPalette.aliceBlue.main },
                }}
                data-testid='camera-close'
              />
              <ToolbarContent>
                {!isPermissionError && helperText && (
                  <HelperText>{helperText}</HelperText>
                )}
              </ToolbarContent>
            </IonToolbar>
          </IonHeader>

          {photo && (
            <PreviewPhoto
              photo={photo}
              onDelete={handlePhotoDelete}
              onOk={handlePhotoSave}
              loading={isLoading}
            />
          )}

          <CameraPreviewContent id='cameraPreview' />

          {isPermissionError && (
            <ErrorMessage>
              <p>
                Разрешите доступ к камере и перезапустите приложение для того
                чтобы иметь возможность делать фотографии и прикреплять их к
                осмотру автомобиля.
              </p>
              <br />
              <Button
                fullWidth
                variant='coralFlooded'
                onClick={handleCameraClose}
              >
                Закрыть
              </Button>
            </ErrorMessage>
          )}

          {!photo && !isPermissionError && (
            <IonFooter>
              <IonToolbar>
                <ToolbarWrapper>
                  <IconButton
                    onClick={handleCameraFlip}
                    icon={cameraReverse}
                    style={{ fontSize: '30px' }}
                    iconProps={{
                      style: {
                        color: myPalette.aliceBlue.main,
                      },
                    }}
                  />

                  <CameraAltButton
                    onClick={handleCameraCapture}
                    icon={camera}
                    iconProps={{ style: { color: myPalette.aliceBlue.main } }}
                    data-testid='camera-capture'
                  />
                </ToolbarWrapper>
              </IonToolbar>
            </IonFooter>
          )}
        </IonPage>
      )}

      <CameraContext.Provider
        value={{
          isCameraOpen,
          cameraOpen: handleCameraOpen,
          cameraClose: handleCameraClose,
          setHelperText: setHelperText,
        }}
      >
        <div hidden={isCameraOpen}>{children}</div>
      </CameraContext.Provider>
    </>
  )
}
